export interface CapsuleHelmCommandInput {
  chartRepository?: string | null;
  version?: string | null;
  httpGatewayEndpointFqdn?: string | null;
  capsuleDockerImage?: string | null;
  alloyTelemetryApiKey?: string | null;
  releaseName?: string | null;
  apiAccessKey?: string | null;
  credentialsDecryptKey?: string | null;
  dataplaneUid?: string | null;
  orgId?: number | string | null;
  diagnosticsDumpsEnabled?: boolean | null;
  diagnosticsDumpsPvcExistingClaim?: string | null;
}

export type StringHelmCommandField = Exclude<
  keyof CapsuleHelmCommandInput,
  'diagnosticsDumpsEnabled'
>;

export interface CapsuleHelmCommandResult {
  status: 'complete' | 'incomplete';
  command: string;
  missingValues: string[];
}

export const CAPSULE_HELM_COMMAND_PLACEHOLDERS: Record<StringHelmCommandField, string> = {
  chartRepository: '<<ADD_CHART_REPOSITORY_HERE>>',
  version: '0.1.5',
  httpGatewayEndpointFqdn: 'dataops-gw.nexla.io',
  capsuleDockerImage: '<<ADD_CAPSULE_DOCKER_IMAGE_HERE>>',
  alloyTelemetryApiKey: '<<ADD_ALLOY_TELEMETRY_API_KEY_HERE>>',
  releaseName: '<<ADD_RELEASE_NAME_HERE>>',
  apiAccessKey: '<<ADD_API_ACCESS_KEY_HERE>>',
  credentialsDecryptKey: '<<ADD_ENCRYPTION_DECRYPTION_KEY_HERE>>',
  dataplaneUid: '<<ADD_DATAPLANE_UID_HERE>>',
  orgId: '<<ADD_ORG_ID_HERE>>',
  diagnosticsDumpsPvcExistingClaim: '<<ADD_PVC_CLAIM_NAME_HERE>>',
};

const REQUIRED_VALUE_LABELS: Record<StringHelmCommandField, string> = {
  chartRepository: 'chart repository',
  version: 'chart version',
  httpGatewayEndpointFqdn: 'HTTP gateway endpoint FQDN',
  capsuleDockerImage: 'Capsule Docker image',
  alloyTelemetryApiKey: 'Alloy telemetry API key',
  releaseName: 'release name',
  apiAccessKey: 'API access key',
  credentialsDecryptKey: 'credentials decrypt key',
  dataplaneUid: 'dataplane UID',
  orgId: 'org ID',
  diagnosticsDumpsPvcExistingClaim: 'PVC claim name',
};

function normalizeValue(value: number | string | null | undefined): string | null {
  if (value == null) return null;

  const stringValue = String(value).trim();
  return stringValue.length > 0 ? stringValue : null;
}

function isPlaceholderValue(value: string): boolean {
  return /^<<ADD_[A-Z0-9_]+_HERE>>$/.test(value);
}

function isMissing(value: string | null): boolean {
  return value == null || isPlaceholderValue(value);
}

function escapeShellUnquotedValue(value: string): string {
  if (isPlaceholderValue(value)) return value;

  return value.replace(/[\s!"#$&'()*;<=>?[\\\]`{|}~]/g, '\\$&');
}

function escapeHelmSetValue(value: string): string {
  if (isPlaceholderValue(value)) return value;

  const helmEscapedValue = value
    .replace(/\\/g, '\\\\')
    .replace(/,/g, '\\,')
    .replace(/{/g, '\\{')
    .replace(/}/g, '\\}');

  return escapeShellUnquotedValue(helmEscapedValue);
}

export interface BuildCapsuleHelmCommandOptions {
  /**
   * Fields rendered verbatim (no shell/Helm escaping). Used only for the masked
   * display preview, where secrets are shown as `xxxx*****yyyy` — the `*` would
   * otherwise be shell-escaped to `\*`. The copied command passes no options, so
   * real values are always fully escaped.
   */
  unescapedFields?: ReadonlySet<StringHelmCommandField>;
}

export function buildCapsuleHelmCommand(
  input: CapsuleHelmCommandInput,
  options?: BuildCapsuleHelmCommandOptions,
): CapsuleHelmCommandResult {
  const helmSetValue = (field: StringHelmCommandField, value: string): string =>
    options?.unescapedFields?.has(field) ? value : escapeHelmSetValue(value);

  const normalizedValues = {
    chartRepository: normalizeValue(input.chartRepository),
    version: normalizeValue(input.version),
    httpGatewayEndpointFqdn: normalizeValue(input.httpGatewayEndpointFqdn),
    capsuleDockerImage: normalizeValue(input.capsuleDockerImage),
    alloyTelemetryApiKey: normalizeValue(input.alloyTelemetryApiKey),
    releaseName: normalizeValue(input.releaseName),
    apiAccessKey: normalizeValue(input.apiAccessKey),
    credentialsDecryptKey: normalizeValue(input.credentialsDecryptKey),
    dataplaneUid: normalizeValue(input.dataplaneUid),
    orgId: normalizeValue(input.orgId),
  };

  const normalizedPvcClaim = normalizeValue(input.diagnosticsDumpsPvcExistingClaim);

  const requiredMissing = (Object.keys(normalizedValues) as (keyof typeof normalizedValues)[])
    .filter((key) => isMissing(normalizedValues[key]))
    .map((key) => REQUIRED_VALUE_LABELS[key]);

  const pvcMissing =
    input.diagnosticsDumpsEnabled && isMissing(normalizedPvcClaim)
      ? [REQUIRED_VALUE_LABELS.diagnosticsDumpsPvcExistingClaim]
      : [];

  const missingValues = [...requiredMissing, ...pvcMissing];

  const values = {
    chartRepository: normalizedValues.chartRepository ?? '',
    version: normalizedValues.version ?? '',
    httpGatewayEndpointFqdn: normalizedValues.httpGatewayEndpointFqdn ?? '',
    capsuleDockerImage: normalizedValues.capsuleDockerImage ?? '',
    alloyTelemetryApiKey: normalizedValues.alloyTelemetryApiKey ?? '',
    releaseName: normalizedValues.releaseName ?? '',
    apiAccessKey: normalizedValues.apiAccessKey ?? '',
    credentialsDecryptKey: normalizedValues.credentialsDecryptKey ?? '',
    dataplaneUid: normalizedValues.dataplaneUid ?? '',
    orgId: normalizedValues.orgId ?? '',
  };
  const pvcClaimValue = normalizedPvcClaim ?? '';
  const lineContinuation = ' \\' + '\n';

  const commandLines = [
    `helm upgrade --install ${escapeShellUnquotedValue(values.releaseName)} ${escapeShellUnquotedValue(
      values.chartRepository,
    )}`,
    `  --version ${escapeShellUnquotedValue(values.version)}`,
    `  --namespace nexla --create-namespace`,
    `  --set http.gateway.endpoint.fqdn=${escapeHelmSetValue(values.httpGatewayEndpointFqdn)}`,
    `  --set capsuleDockerImage=${escapeHelmSetValue(values.capsuleDockerImage)}`,
    `  --set alloy.telemetryAPIKey=${helmSetValue('alloyTelemetryApiKey', values.alloyTelemetryApiKey)}`,
    `  --set api.access.key=${helmSetValue('apiAccessKey', values.apiAccessKey)}`,
    `  --set credentials.decrypt.key=${helmSetValue('credentialsDecryptKey', values.credentialsDecryptKey)}`,
    `  --set dataplane.uid=${escapeHelmSetValue(values.dataplaneUid)}`,
    `  --set org.id=${escapeHelmSetValue(values.orgId)}`,
  ];

  if (input.diagnosticsDumpsEnabled) {
    commandLines.push(`  --set diagnostics.dumps.enabled=true`);
    commandLines.push(
      `  --set diagnostics.dumps.pvc.existingClaim=${escapeHelmSetValue(pvcClaimValue)}`,
    );
  }

  return {
    status: missingValues.length > 0 ? 'incomplete' : 'complete',
    command: commandLines.join(lineContinuation),
    missingValues,
  };
}
